import {
  afterNextRender,
  Component,
  computed,
  effect,
  ElementRef,
  inject,
  Injector,
  Input,
  OnInit,
  viewChild,
} from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { MatTabGroup, MatTabsModule } from '@angular/material/tabs';
import { ActionsMenu } from 'src/app/components/common-actions/common-actions.component';
import { ComponentsModule } from 'src/app/components/components.module';
import { RvSpinnerDirective } from 'src/app/components/loading/rv-spinner.directive';
import { NotificationAlertComponent } from 'src/app/components/notification-alert/notification-alert.component';
import { BaseEditableComponent } from 'src/app/shared/components/base-editable/base-editable.component';
import { SharedModule } from 'src/app/shared/shared.module';
import { DISPLAY_ACCESS } from 'src/app/auth/types/access-level';
import {
  CapAlertsReturnTarget,
  INTEGRATIONS_LIST_STATE,
} from '../../../services/integrations/integrations-company/integrations-company.routing';
import { CapAlertsPageService } from '../../../services/cap-alerts/cap-alerts-page.service';
import { CapAlertsTab } from '../../../types/cap-alerts-page.types';
import { CapAlertPreviewComponent } from '../cap-alert-preview/cap-alert-preview.component';
import { CapAlertTemplatePickerComponent } from '../cap-alert-template-picker/cap-alert-template-picker.component';
import { CapAlertsDisclaimerComponent } from '../cap-alerts-disclaimer/cap-alerts-disclaimer.component';
import { CapAlertsConnectTabComponent } from '../cap-alerts-connect-tab/cap-alerts-connect-tab.component';
import { CapAlertsSettingsTabComponent } from '../cap-alerts-settings-tab/cap-alerts-settings-tab.component';

/** Tabs in display order; Customize joins once templates can be customized. */
const TABS: CapAlertsTab[] = ['connect', 'settings'];

@Component({
  selector: 'cap-alerts-page',
  templateUrl: './cap-alerts-page.component.html',
  styleUrl: './cap-alerts-page.component.scss',
  imports: [
    MatIconModule,
    MatTabsModule,
    ComponentsModule,
    SharedModule,
    NotificationAlertComponent,
    RvSpinnerDirective,
    CapAlertsConnectTabComponent,
    CapAlertsSettingsTabComponent,
    CapAlertsDisclaimerComponent,
    CapAlertTemplatePickerComponent,
    CapAlertPreviewComponent,
  ],
  providers: [CapAlertsPageService],
})
export class CapAlertsPageComponent
  extends BaseEditableComponent<unknown>
  implements OnInit
{
  /** Where Cancel on the disclaimer goes; the route resolves it. */
  @Input() capAlertsReturnTarget?: CapAlertsReturnTarget;

  protected readonly pageService = inject(CapAlertsPageService);
  protected readonly exportAccess = DISPLAY_ACCESS.FULL_ACCESS;

  private readonly injector = inject(Injector);
  private readonly disclaimer = viewChild(CapAlertsDisclaimerComponent);
  private readonly picker = viewChild(CapAlertTemplatePickerComponent);
  private readonly tabGroup = viewChild<MatTabGroup, ElementRef<HTMLElement>>(
    MatTabGroup,
    { read: ElementRef },
  );
  private readonly changeTemplateButton = viewChild<
    string,
    ElementRef<HTMLButtonElement>
  >('changeTemplateButton', { read: ElementRef });
  private readonly retryButton = viewChild<
    string,
    ElementRef<HTMLButtonElement>
  >('retryButton', { read: ElementRef });
  private previousView = this.pageService.view();

  protected readonly selectedIndex = computed(() =>
    TABS.indexOf(this.pageService.activeTab()),
  );

  /** Rebuilt on every change because `common-actions` reads `disabled` as a snapshot. */
  protected readonly actions = computed<ActionsMenu>(() => ({
    saveAction: {
      id: 'saveButton',
      label: 'Save',
      action: () => void this.pageService.save(),
      disabled:
        this.pageService.loading() ||
        this.pageService.readOnly() ||
        this.pageService.saving(),
      requireRole: 'ua',
    },
    cancelAction: {
      id: 'cancelButton',
      label: 'Cancel',
      action: () => this.pageService.cancel(),
    },
  }));

  constructor() {
    super();

    // The views replace one another inside one route, which drops the control
    // the user just activated; focus follows the view that took its place.
    effect(() => {
      const view = this.pageService.view();
      const previous = this.previousView;
      this.previousView = view;

      // Loading into a view is a plain page load: it must not take focus.
      if (previous === 'loading' || previous === view) {
        return;
      }
      afterNextRender(() => this.moveFocusTo(view), {
        injector: this.injector,
      });
    });
  }

  ngOnInit(): void {
    void this.pageService.init();
  }

  canDeactivate(): boolean {
    return !this.pageService.isDirty();
  }

  protected cancelDisclaimer(): void {
    this.bypassUnsavedChanges();
    const target = this.capAlertsReturnTarget ?? {
      state: INTEGRATIONS_LIST_STATE,
      params: {},
    };
    void this.stateService.go(target.state, target.params);
  }

  /**
   * Retry removes the banner and its button, which drops focus; a user-initiated
   * retry, unlike a plain page load, leaves focus where the outcome is.
   */
  protected async retry(): Promise<void> {
    await this.pageService.retry();
    afterNextRender(() => this.moveFocusAfterRetry(), {
      injector: this.injector,
    });
  }

  private moveFocusAfterRetry(): void {
    if (this.pageService.error()?.kind === 'catalog') {
      this.retryButton()?.nativeElement.focus();
      return;
    }
    const view = this.pageService.view();
    // The picker is reached from the disclaimer, a view change that already
    // moves focus on its own.
    if (view === 'page' || view === 'disclaimer') {
      this.moveFocusTo(view);
    }
  }

  private moveFocusTo(view: string): void {
    switch (view) {
      case 'disclaimer':
        this.disclaimer()?.focusHeading();
        break;
      case 'picker':
        this.picker()?.focusHeading();
        break;
      case 'page': {
        const button = this.changeTemplateButton()?.nativeElement;
        if (
          this.pageService.pickerMode() === 'change' &&
          button &&
          !button.disabled
        ) {
          button.focus();
        } else {
          // `MatTabGroup.focusTab` does nothing for the tab that is already
          // active, which is the one this needs.
          this.tabGroup()
            ?.nativeElement.querySelector<HTMLElement>(
              '[role="tab"][aria-selected="true"]',
            )
            ?.focus();
        }
        break;
      }
    }
  }

  protected selectTab(index: number): void {
    this.pageService.activeTab.set(TABS[index]);
  }
}
